import React from 'react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, ShoppingBag, Wallet, CreditCard, ArrowRight } from 'lucide-react';
import { monthlySpending, netWorthHistory, spendingByCategory, transactions, budgetCategories } from '../../../data/mockData';
function StatCard({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  iconBg,
  positive
}: any) {
  return <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm hover:shadow-md transition-shadow" data-id="element-568">
      <div className="flex items-start justify-between mb-4" data-id="element-569">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`} data-id="element-570">
          <Icon size={18} className="text-white" data-id="element-571" />
        </div>
        <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${positive ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'}`} data-id="element-572">
          {positive ? <TrendingUp size={11} data-id="element-573" /> : <TrendingDown size={11} data-id="element-574" />}
          {change}
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-575">
        {value}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-576">{title}</p>
      <p className="text-xs text-gray-400 mt-0.5" data-id="element-577">{changeLabel}</p>
    </div>;
}
const COLORS = ['#EF4444', '#F59E0B', '#EC4899', '#06B6D4', '#10B981', '#3B82F6', '#8B5CF6'];
export function OverviewView() {
  const recentTx = transactions.slice(0, 5);
  return <div className="space-y-6" data-id="element-578">
      {/* Welcome */}
      <div data-id="element-579">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-580">
          Good morning, Alex 👋
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-581">
          Here's your financial snapshot for February 2026
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-582">
        <StatCard title="Net Worth" value="$76,420" change="+11.1%" changeLabel="vs last year" icon={Wallet} iconBg="bg-gradient-to-br from-primary-500 to-primary-700" positive={true} data-id="element-583" />
        <StatCard title="Monthly Spending" value="$5,317" change="+8.2%" changeLabel="vs last month" icon={ShoppingBag} iconBg="bg-gradient-to-br from-red-400 to-red-600" positive={false} data-id="element-584" />
        <StatCard title="Monthly Income" value="$7,489" change="+2.1%" changeLabel="vs last month" icon={DollarSign} iconBg="bg-gradient-to-br from-accent-400 to-accent-600" positive={true} data-id="element-585" />
        <StatCard title="Credit Score" value="742" change="+4 pts" changeLabel="vs last month" icon={CreditCard} iconBg="bg-gradient-to-br from-purple-400 to-purple-600" positive={true} data-id="element-586" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-587">
        {/* Cash flow chart */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-588">
          <div className="flex items-center justify-between mb-4" data-id="element-589">
            <div data-id="element-590">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-591">
                Cash Flow
              </h3>
              <p className="text-xs text-gray-400" data-id="element-592">Income vs Expenses</p>
            </div>
            <span className="text-xs bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-400 px-2 py-1 rounded-full font-medium" data-id="element-593">
              +$2,172 saved
            </span>
          </div>
          <ResponsiveContainer width="100%" height={180} data-id="element-594">
            <AreaChart data={monthlySpending} data-id="element-595">
              <defs data-id="element-596">
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-597">
                  <stop offset="5%" stopColor="#1566FF" stopOpacity={0.2} data-id="element-598" />
                  <stop offset="95%" stopColor="#1566FF" stopOpacity={0} data-id="element-599" />
                </linearGradient>
                <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-600">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.15} data-id="element-601" />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0} data-id="element-602" />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{
              fontSize: 11,
              fill: '#9CA3AF'
            }} axisLine={false} tickLine={false} data-id="element-603" />
              <YAxis tick={{
              fontSize: 11,
              fill: '#9CA3AF'
            }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-604" />
              <Tooltip formatter={(v: any) => [`$${v.toLocaleString()}`, '']} contentStyle={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              fontSize: '12px'
            }} data-id="element-605" />
              <Area type="monotone" dataKey="income" stroke="#1566FF" strokeWidth={2} fill="url(#incomeGrad)" name="Income" data-id="element-606" />
              <Area type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} fill="url(#expenseGrad)" name="Expenses" data-id="element-607" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Spending by category */}
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-608">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1" data-id="element-609">
            Spending Breakdown
          </h3>
          <p className="text-xs text-gray-400 mb-3" data-id="element-610">This month</p>
          <ResponsiveContainer width="100%" height={140} data-id="element-611">
            <PieChart data-id="element-612">
              <Pie data={spendingByCategory} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="value" data-id="element-613">
                {spendingByCategory.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-614" />)}
              </Pie>
              <Tooltip formatter={(v: any) => [`$${v.toLocaleString()}`, '']} contentStyle={{
              borderRadius: '8px',
              border: 'none',
              fontSize: '11px'
            }} data-id="element-615" />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2" data-id="element-616">
            {spendingByCategory.slice(0, 4).map((cat, i) => <div key={i} className="flex items-center justify-between" data-id="element-617">
                <div className="flex items-center gap-2" data-id="element-618">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{
                backgroundColor: cat.color
              }} data-id="element-619" />
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-620">
                    {cat.name}
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-621">
                  ${cat.value.toLocaleString()}
                </span>
              </div>)}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-622">
        {/* Recent transactions */}
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-623">
          <div className="flex items-center justify-between mb-4" data-id="element-624">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-625">
              Recent Transactions
            </h3>
            <button className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1 font-medium" data-id="element-626">
              View all <ArrowRight size={12} data-id="element-627" />
            </button>
          </div>
          <div className="space-y-3" data-id="element-628">
            {recentTx.map(tx => <div key={tx.id} className="flex items-center gap-3" data-id="element-629">
                <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-dark-hover flex items-center justify-center text-base shrink-0" data-id="element-630">
                  {tx.icon}
                </div>
                <div className="flex-1 min-w-0" data-id="element-631">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" data-id="element-632">
                    {tx.merchant}
                  </p>
                  <p className="text-xs text-gray-400" data-id="element-633">
                    {tx.category} · {tx.date}
                  </p>
                </div>
                <span className={`text-sm font-semibold shrink-0 ${tx.type === 'credit' ? 'text-accent-600 dark:text-accent-400' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-634">
                  {tx.type === 'credit' ? '+' : '-'}$
                  {Math.abs(tx.amount).toFixed(2)}
                </span>
              </div>)}
          </div>
        </div>

        {/* Budget progress */}
        <div className="bg-white dark:bg-dark-card rounded-2xl p-5 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-635">
          <div className="flex items-center justify-between mb-4" data-id="element-636">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-637">
              Budget Status
            </h3>
            <button className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1 font-medium" data-id="element-638">
              Manage <ArrowRight size={12} data-id="element-639" />
            </button>
          </div>
          <div className="space-y-3" data-id="element-640">
            {budgetCategories.slice(0, 5).map(cat => {
            const pct = Math.min(cat.spent / cat.limit * 100, 100);
            const isOver = cat.spent > cat.limit;
            const barColor = isOver ? '#EF4444' : pct > 80 ? '#F59E0B' : '#39E75F';
            return <div key={cat.id} data-id="element-641">
                  <div className="flex items-center justify-between mb-1" data-id="element-642">
                    <span className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5" data-id="element-643">
                      <span data-id="element-644">{cat.icon}</span> {cat.name}
                    </span>
                    <span className="text-xs font-medium" style={{
                  color: isOver ? '#EF4444' : '#6B7280'
                }} data-id="element-645">
                      ${cat.spent.toFixed(0)} / ${cat.limit}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-dark-hover rounded-full overflow-hidden" data-id="element-646">
                    <div className="h-full rounded-full transition-all" style={{
                  width: `${pct}%`,
                  backgroundColor: barColor
                }} data-id="element-647" />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}